import { useState } from "react";
import { DotLegalOverflowMenuItem, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { htmlToPlainText } from "@common/stringOperations";
import { useRowNoteDialog } from "@common/components/dotlegalNote/useRowNoteDialog";
import { EntityType } from "@common/Common.types";
import { InternalAssessmentApiRow, InternalAssessmentViewRow } from "../../../../internalAssessment/InternalAssessments.types";
import { LegalEntityAuditStatus } from "../../../../legalEntity/legalEntityAuditTab/LegalEntityAudit.types";
import { SelectableItem } from "../../../../processingActivity/ProcessingActivity.types";
import { put } from "../../../api/apiShared";
import { getQuestionnaireResponseScore } from "../../../questionnaireResponseScore";
import { useTranslation } from "../../../../localization/useTranslation";
import { InternalAssessmentTableContentProps } from "./InternalAssessmentTable";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { AuditFieldGrouping, FilterOption, FilterOptionType } from "../../filterPopover/FilterPopover.types";
import { getScoreFilterFixedData, getUniqueFilterOptions, matchesScoreFilter, sortByEnumValue } from "@common/hooks/filterUtils.ts";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, hasDateRange, matchesDateFilter, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import {
    assessmentGroupColumnOrder,
    assessmentGroupHiddenSearchFields,
    fullColumnOrder,
    getFilterIds,
    getSelectableColumns,
    InternalAssessmentColumnKey,
} from "./InternalAssessmentTable.columns.ts";
import { useStateSessionStorage } from "@common/hooks/useStateSessionStorage";
import { FilterPage } from "@common/pageView/PageView.types";
import { toTableFields, useClientFilteredPageView } from "@common/pageView/useClientFilteredPageView";
import { noteExcludedFromSearch } from "@common/tableSearch";
import { exportDisplayedRows } from "@common/auditExport";
import { getDisplayedRiskLevel } from "../../../../internalAssessment/InternalAssessments.utils.ts";
import { useDownloadInternalAssessmentReport } from "../../../../internalAssessment/DownloadInternalAssessmentReport.hooks";

export const defaultSortProperty = "sent";
export const defaultSortOrder = "desc";

export function useInternalAssessmentTablePageView(page: FilterPage, showPublicTemplateColumn: boolean | undefined) {
    return useClientFilteredPageView<InternalAssessmentColumnKey>({
        page,
        availableColumns: getSelectableColumns(showPublicTemplateColumn),
        defaultOrderBy: defaultSortProperty,
        defaultOrder: defaultSortOrder,
    });
}

export default function useInternalAssessmentTableHooks(props: InternalAssessmentTableContentProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateLegalEntityAuditStatus, translateDate, translateString, translateEvaluationEnum } = useTranslation();
    const { onDownloadReportAsPdf, getReportPdfUrl, isDownloadingPdf } = useDownloadInternalAssessmentReport();
    const [searchString, setSearchString] = useStateSessionStorage<string>(`${props.sessionStorageKey ?? "internal-assessments"}-search`, "");

    const [selectedInternalAssessment, setSelectedInternalAssessment] = useState<InternalAssessmentApiRow | undefined>(undefined);
    const [showNote, setShowNote] = useState(false);
    const pageView = props.pageView;
    // With saved views the view owns the filters, so the session-only state is left without a key and never written.
    const sessionFilterState = useFilterState(pageView ? undefined : (props.sessionStorageKey ?? "internal-assessment-filters"));
    const { selectFilters: filters, dateFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = pageView ?? sessionFilterState;
    const isLoading = props.isLoading || !!pageView?.savedViewsAreLoading;

    const activeFilterIds = getFilterIds(props.withinAssessmentGroup);
    const activeFilterIdLookup = new Set<string>(activeFilterIds);
    const isActiveFilter = (id: string) => activeFilterIdLookup.has(id);

    let audits;
    let selectableStatus: Array<SelectableItem> = [];
    let selectableEvaluationStatus: Array<SelectableItem> = [];
    let selectableYearsSent: Array<SelectableItem> = [];
    let selectableRiskLevels: Array<SelectableItem> = [];
    if (props.internalAssessmentModel) {
        selectableStatus = getSelectableStatus();
        selectableEvaluationStatus = getSelectableEvaluationStatus();
        selectableYearsSent = getSelectableYearsSent();
        selectableRiskLevels = getSelectableRiskLevels();
        audits = applyClientFilters(
            props.internalAssessmentModel.rows,
            [
                { id: "names", candidate: (r: InternalAssessmentApiRow) => r.id.toLowerCase() },
                { id: "templates", candidate: (r: InternalAssessmentApiRow) => r.questionnaireId },
                { id: "areas", candidate: (r: InternalAssessmentApiRow) => r.complianceAreas.map((x) => x.id) },
                { id: "status", candidate: (r: InternalAssessmentApiRow) => r.status.toString() },
                { id: "riskLevels", candidate: (r: InternalAssessmentApiRow) => getDisplayedRiskLevel(r, translateString)?.id },
                { id: "evaluations", candidate: (r: InternalAssessmentApiRow) => r.evaluationStatus?.toString() },
                { id: "responsibles", candidate: (r: InternalAssessmentApiRow) => r.responsible.id },
                { id: "assignees", candidate: (r: InternalAssessmentApiRow) => r.assignee.id.toLowerCase() },
                { id: "senders", candidate: (r: InternalAssessmentApiRow) => r.sender?.toLowerCase() },
                { id: "yearsSent", candidate: (r: InternalAssessmentApiRow) => (r.sent ? new Date(r.sent).getFullYear().toString() : undefined) },
            ].filter((mapping) => isActiveFilter(mapping.id)),
            filters,
            {},
            filterOperators,
            // Score buckets and the sent date range need their own predicate rather than a candidate, so they answer
            // to the same id list the rest do instead of being the filters a mode cannot switch off. Only one of the
            // two is ever active, but they share the single predicate slot either way.
            (row) =>
                (!isActiveFilter("score") || matchesScoreFilter(filters.score ?? [], filterOperators.score, row)) &&
                (!isActiveFilter("sentDateRange") || matchesDateFilter(dateFilters.sentDateRange, row.sent))
        )!;
    }

    const saveNoteMutation = useDotLegalMutation(submitSaveNote);
    function submitSaveNote(note: string) {
        return put(`/internalAssessments/${selectedInternalAssessment!.id}/note`, note);
    }

    const saveNote = (note: string) => {
        saveNoteMutation.mutateAsync(note, {
            onSuccess: () => {
                snackbar.show(translateString("noteUpdated"));
                setShowNote(false);
                props.refetch!();
            },
        });
    };

    const auditsViewModelRows: undefined | Array<InternalAssessmentViewRow> = audits?.map((r) => {
        return {
            ...r,
            responsibleName: r.responsible.name,
            assigneeName: r.assignee.name,
            complianceAreasString: r.complianceAreas.map((area) => area.name).join(", "),
            questionnaireName: r.assessmentTemplate.name,
            statusString: translateLegalEntityAuditStatus(r.status.toString()),
            scoreName: getQuestionnaireResponseScore(
                r.status === LegalEntityAuditStatus.Submitted,
                r.scoreEnabled,
                r.score,
                translateString("scoreNotEnabled")
            ),
            evaluationName: r.evaluationStatus ? translateEvaluationEnum(r.evaluationStatus.toString()) : "",
            sentString: translateDate(r.sent),
            deadlineString: translateDate(r.deadline),
            reminderSentString: r.reminderSent ? translateDate(r.reminderSent) : "",
            notePlainText: htmlToPlainText(r.note),
        };
    });

    const columnLabels: Record<InternalAssessmentColumnKey, string> = {
        name: translateString("name"),
        uniqueIdentifier: "Id",
        sent: translateString("assessmentSent"),
        deadline: translateString("deadline"),
        responsibleName: translateString("responsible"),
        assigneeName: translateString("assigned"),
        questionnaireName: translateString("assessmentTemplate"),
        complianceAreas: translateString("complianceAreas"),
        sender: translateString("sentFrom"),
        publicTemplateName: translateString("publicInternalAssessment"),
        status: translateString("status"),
        riskLevel: translateString("riskLevel"),
        score: translateString("score"),
        assessmentReport: translateString("assessmentReport"),
        evaluation: translateString("evaluation"),
        evaluationName: "",
        reminderSent: translateString("headerReminderSent"),
        note: translateString("note"),
    };

    // evaluationName has no cell of its own; it rides along after the chosen columns so sorting and search keep it.
    function getColumnOrder(): Array<InternalAssessmentColumnKey> {
        if (props.withinAssessmentGroup) {
            return assessmentGroupColumnOrder;
        }

        if (pageView) {
            return [...pageView.renderedColumns, "evaluationName"];
        }

        return fullColumnOrder.filter((key) => key !== "publicTemplateName" || props.showPublicTemplateColumn);
    }

    const tableFields = toTableFields(getSelectableColumns(props.showPublicTemplateColumn), columnLabels, AuditFieldGrouping.InternalAssessment);

    const searchExclusions: Array<keyof InternalAssessmentViewRow> = props.withinAssessmentGroup
        ? [...assessmentGroupHiddenSearchFields, ...noteExcludedFromSearch]
        : noteExcludedFromSearch;

    async function onExportClick() {
        await exportDisplayedRows({
            path: "/internalAssessments/Export",
            reportName: props.savedViews!.reportName,
            pageView: pageView!,
            columnLabels,
            rows: auditsViewModelRows ?? [],
            searchString,
            searchExclusions,
        });
    }

    const { getNoteMenuItem, noteDialogProps } = useRowNoteDialog<InternalAssessmentApiRow>({
        getPostUrl: (row) => `/internalAssessments/${row.id}/note`,
        entityType: EntityType.InternalAssessment,
        onSaved: () => props.refetch!(),
    });

    // The note has no other home than this table, so its menu is the way in. Only where the note can be edited:
    // a row outside the current plan is locked, and a read-only table edits nothing.
    function getMenuItems(row: InternalAssessmentApiRow): Array<DotLegalOverflowMenuItem> {
        const callerItems = props.getMenuItems(row);
        if (props.readOnly || !row.partOfCurrentPlan) {
            return callerItems;
        }

        return [...callerItems, getNoteMenuItem(row)];
    }

    function getFilterOptions(): Array<FilterOption> {
        const rows = props.internalAssessmentModel?.rows;
        return withOperators(
            [
                {
                    id: "names",
                    label: translateString("name"),
                    type: FilterOptionType.SelectField,
                    value: filters.names ?? [],
                    fixedData: getUniqueFilterOptions(
                        rows,
                        (r) => r.name,
                        (r) => r.id.toLowerCase()
                    ),
                },
                {
                    id: "yearsSent",
                    label: translateString("sent"),
                    type: FilterOptionType.SelectField,
                    value: filters.yearsSent ?? [],
                    fixedData: selectableYearsSent.map((x) => ({ id: x.id, name: x.name })),
                },
                // The group's counterpart to yearsSent. Only one of the two is ever in the active id list, so the
                // popover shows a year list or a date range, never both.
                {
                    id: "sentDateRange",
                    label: translateString("sent"),
                    type: FilterOptionType.DateField,
                    value: dateFilters.sentDateRange ?? null,
                    hideDatePresets: true,
                },
                {
                    id: "responsibles",
                    label: translateString("responsible"),
                    type: FilterOptionType.SelectField,
                    value: filters.responsibles ?? [],
                    fixedData: props.internalAssessmentModel?.selectableResponsibles?.map((x) => ({ id: x.id, name: x.name })) ?? [],
                },
                {
                    id: "assignees",
                    label: translateString("assigned"),
                    type: FilterOptionType.SelectField,
                    value: filters.assignees ?? [],
                    fixedData: getUniqueFilterOptions(
                        rows,
                        (r) => r.assignee.name,
                        (r) => r.assignee.id.toLowerCase()
                    ),
                },
                {
                    id: "templates",
                    label: translateString("assessmentTemplate"),
                    type: FilterOptionType.SelectField,
                    value: filters.templates ?? [],
                    fixedData: props.internalAssessmentModel?.selectableAssessmentTemplate?.map((x) => ({ id: x.id, name: x.name })) ?? [],
                },
                {
                    id: "areas",
                    label: translateString("complianceAreas"),
                    type: FilterOptionType.SelectField,
                    value: filters.areas ?? [],
                    fixedData: props.internalAssessmentModel?.selectableComplianceAreas?.map((x) => ({ id: x.id, name: x.name })) ?? [],
                },
                {
                    id: "senders",
                    label: translateString("sentFrom"),
                    type: FilterOptionType.SelectField,
                    value: filters.senders ?? [],
                    fixedData: getUniqueFilterOptions(
                        rows,
                        (r) => r.sender ?? undefined,
                        (r) => r.sender?.toLowerCase()
                    ),
                },
                {
                    id: "status",
                    label: translateString("status"),
                    type: FilterOptionType.SelectField,
                    value: filters.status ?? [],
                    fixedData: selectableStatus.map((x) => ({ id: x.id, name: x.name })),
                },
                {
                    id: "riskLevels",
                    label: translateString("riskLevel"),
                    type: FilterOptionType.SelectField,
                    value: filters.riskLevels ?? [],
                    fixedData: selectableRiskLevels.map((x) => ({ id: x.id, name: x.name })),
                },
                {
                    id: "score",
                    label: translateString("score"),
                    type: FilterOptionType.SelectField,
                    value: filters.score ?? [],
                    fixedData: getScoreFilterFixedData(translateString),
                },
                {
                    id: "evaluations",
                    label: translateString("evaluation"),
                    type: FilterOptionType.SelectField,
                    value: filters.evaluations ?? [],
                    fixedData: selectableEvaluationStatus.map((x) => ({ id: x.id, name: x.name })),
                },
            ].filter((option) => isActiveFilter(option.id)),
            filterOperators
        );
    }

    // A date range lives in its own bucket rather than in the select filters, so the empty state has to look there
    // too - otherwise a range that matches nothing reads as "no assessments" instead of "none match the criteria".
    const hasActiveFilters = activeFilterIds.some((id) => (filters[id] ?? []).length > 0 || hasDateRange(dateFilters[id]));

    return {
        isLoading,
        searchString,
        setSearchString,
        columnLabels,
        columnOrder: getColumnOrder(),
        searchExclusions,
        tableFields,
        pageView,
        onExportClick,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        audits: auditsViewModelRows,
        hasData: isLoading || (props.internalAssessmentModel?.rows && props.internalAssessmentModel.rows.length > 0),
        filterOptions: getFilterOptions(),
        onFilterChange,
        selectedInternalAssessment,
        setSelectedInternalAssessment,
        getReportPdfUrl,
        isDownloadingPdf,
        onDownloadReportAsPdf,
        showNote,
        setShowNote,
        saveNote,
        savingNote: saveNoteMutation.isLoading,
        hasActiveFilters,
        getMenuItems,
        noteDialogProps,
    };

    function getSelectableStatus() {
        var temp = props.internalAssessmentModel!.rows.map((x) => {
            return { id: x.status.toString(), name: translateLegalEntityAuditStatus(x.status.toString()) };
        });

        return temp?.filter((item, index) => temp?.findIndex((f) => f.id === item.id) === index);
    }

    function getSelectableEvaluationStatus() {
        var temp = props
            .internalAssessmentModel!.rows.filter((x) => x.evaluationStatus)
            .map((x) => {
                return {
                    id: x.evaluationStatus!.toString(),
                    name: translateEvaluationEnum(x.evaluationStatus!.toString()),
                };
            });

        return temp?.filter((item, index) => temp?.findIndex((f) => f.id === item.id) === index);
    }

    // Built from what the risk cell actually renders, so the filter can never offer a level the column does not
    // show, and never hides a row whose level is on screen.
    function getSelectableRiskLevels() {
        const displayed = props.internalAssessmentModel!.rows.map((x) => getDisplayedRiskLevel(x, translateString)).filter((x) => x !== undefined);

        return displayed.filter((item, index) => displayed.findIndex((f) => f.id === item.id) === index).sort(sortByEnumValue);
    }

    function getSelectableYearsSent() {
        const years = props.internalAssessmentModel!.rows.filter((x) => x.sent).map((x) => new Date(x.sent).getFullYear().toString());
        const uniqueYears = [...new Set(years)].sort((a, b) => b.localeCompare(a));
        return uniqueYears.map((year) => ({ id: year, name: year }));
    }
}
